import { ExporttocsvService, csvDate } from '../../services/exporttocsv/exporttocsv.service';
import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {TeamsService} from '../../services/teams/teams.service';
import {MatSort} from '@angular/material/sort';
import {SessionService} from '../../services/session/session.service';
import {StripeService} from '../../services/stripe/stripe.service';
import {ApiService} from '../../services/api/api.service';
import {UiAlertService} from '../../services/ui-alert/ui-alert.service';
import {SnackBarComponent} from '../snackbar/snackbar.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {SetTeamCompanyComponentModel, SetTeamCompanyDialogComponent} from '../modal/setteamcompanydialog/setteamcompanydialog.component';
import {TeamCompaniesService} from '../../services/teamcompanies/teamcompanies.service';
import {SetTeamPrivacyComponentModel, SetTeamPrivacyDialogComponent} from '../modal/setteamprivacydialog/setteamprivacydialog.component';
import {SendMode} from '../modal/sendfundsdialog/send-funds-dialog.page';
import {ActivatedRoute, Router} from '@angular/router';
import {SetInvoicingComponentModel, SetInvoicingDialogComponent} from '../modal/setinvoicingdialog/setinvoicingdialog.component';
import {ClientService} from '../../services/client/client.service';
import {CustomerLinkModal, CustomerLinkModalComponent} from '../modal/customer-link-modal/customer-link-modal.component';
import {GetUserDialogComponent} from '../modal/getuserdialog/getuserdialog.component';
import {ManagedFeatureFlagsService} from '../../services/managed-feature-flags/managed-feature-flags.service';
import {
  TEAM_ADMIN_TRANSACTION_RECONCILIATION_FLAG,
} from '../../services/managed-feature-flags/managed-feature-flag-keys';

interface TeamInfo {
  id: string;
  name: string;
  memberCount: number;
  ownerName: string;
  ownerUserId: string;
}

type TransactionReconciliationAccessStatus = 'pending' | 'enabled' | 'disabled' | 'error';

interface TransactionReconciliationAccess {
  teamId: string;
  userId: string;
  sessionIdentity: string;
  evaluationIdentity: object;
  status: TransactionReconciliationAccessStatus;
}

@Component({
    selector: 'app-teampayments',
    templateUrl: './teampayments.component.html',
    styleUrls: ['./teampayments.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TeampaymentsComponent implements OnInit, OnDestroy {

  // @ts-ignore
  @ViewChild(MatSort) sort: MatSort;

  public items = new MatTableDataSource<TeamInfo>();
  public columnsToDisplay = ['name', 'memberCount', 'client', 'company', 'ownerName', 'createdAt', 'approved', 'isInvoicePlan', 'actionmenu'];
  public filter = '';
  private currentWellspace: any;
  private companies = [];
  private readonly transactionReconciliationAccessByTeam = new Map<string, TransactionReconciliationAccess>();
  private transactionReconciliationEvaluationGeneration = 0;
  private destroyed = false;


  exportCsv(title: string): void {
    this.csvExport.exportTable(title, this.items, {
      "name": {header: "Team Name", value: (item: any) => item.name},
      "memberCount": {header: "# Members", value: (item: any) => item.memberCount},
      "client": {header: "Client", value: (item: any) => item.client && item.client.name ? item.client.name : '---'},
      "company": {header: "Company", value: (item: any) => item.companyName},
      "ownerName": {header: "Team Owner Name", value: (item: any) => item.ownerName},
      "createdAt": {header: "Created", value: (item: any) => csvDate(item.createdAt, 'medium')},
      "approved": {header: "Approved", value: (item: any) => item.approved ? 'Yes' : 'No'},
      "isInvoicePlan": {header: "Invoicing", value: (item: any) => item.isInvoicePlan ? 'Yes' : 'No'},
    }, this.columnsToDisplay, {exclude: ['action', 'actionmenu']});
  }

  constructor(
      private teamsService: TeamsService,
      private sessionService: SessionService,
      private snackBar: MatSnackBar,
      private dialog: MatDialog,
      private stripeService: StripeService,
      private apiService: ApiService,
      private uiAlertService: UiAlertService,
      private teamCompaniesService: TeamCompaniesService,
      private router: Router,
      private route: ActivatedRoute,
      private clientService: ClientService,
      private managedFeatureFlagsService: ManagedFeatureFlagsService,
      private csvExport: ExporttocsvService
  ) {
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.filter = params.filter;
    });
    this.getAllTeams();
    this.teamsService.wellspaceChangedObservable.subscribe(wellspace => {
      this.currentWellspace = wellspace;
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.invalidateTransactionReconciliationAccess();
  }

  public async refreshTransactionReconciliationAccess(teamId: string): Promise<void> {
    const userId = this.sessionService.getUserId();
    const sessionIdentity = this.getSessionIdentity();
    const evaluationGeneration = this.transactionReconciliationEvaluationGeneration;
    if (this.destroyed || typeof userId !== 'string' || !userId
      || typeof teamId !== 'string' || !teamId
      || !this.items.data.some((team) => team.id === teamId)) {
      return;
    }

    // Check only the opened menu's team. The service shares any matching pending request.
    const evaluationIdentity = {};
    this.transactionReconciliationAccessByTeam.set(teamId, {
      teamId,
      userId,
      sessionIdentity,
      evaluationIdentity,
      status: 'pending',
    });
    try {
      const enabled = await this.managedFeatureFlagsService.isEnabled(
        TEAM_ADMIN_TRANSACTION_RECONCILIATION_FLAG,
        'team-admin',
        { userId, teamId },
      );
      if (!this.isCurrentTransactionReconciliationEvaluation(
        userId,
        sessionIdentity,
        teamId,
        evaluationGeneration,
        evaluationIdentity,
      )) {
        return;
      }
      this.transactionReconciliationAccessByTeam.set(teamId, {
        teamId,
        userId,
        sessionIdentity,
        evaluationIdentity,
        status: enabled === true ? 'enabled' : 'disabled',
      });
    } catch (_error) {
      if (!this.isCurrentTransactionReconciliationEvaluation(
        userId,
        sessionIdentity,
        teamId,
        evaluationGeneration,
        evaluationIdentity,
      )) {
        return;
      }
      this.transactionReconciliationAccessByTeam.set(teamId, {
        teamId,
        userId,
        sessionIdentity,
        evaluationIdentity,
        status: 'error',
      });
      console.error('Failed to evaluate Transaction Reconciliation action access');
    }
  }

  public isTransactionReconciliationEnabled(teamId: string): boolean {
    const access = this.transactionReconciliationAccessByTeam.get(teamId);
    return !this.destroyed
      && !!access
      && access.teamId === teamId
      && access.userId === this.sessionService.getUserId()
      && access.sessionIdentity === this.getSessionIdentity()
      && access.status === 'enabled'
      && this.items.data.some((team) => team.id === teamId);
  }

  private isCurrentTransactionReconciliationEvaluation(
    userId: string,
    sessionIdentity: string,
    teamId: string,
    evaluationGeneration: number,
    evaluationIdentity: object,
  ): boolean {
    const access = this.transactionReconciliationAccessByTeam.get(teamId);
    return !this.destroyed
      && this.transactionReconciliationEvaluationGeneration === evaluationGeneration
      && this.sessionService.getUserId() === userId
      && this.getSessionIdentity() === sessionIdentity
      && !!access
      && access.userId === userId
      && access.sessionIdentity === sessionIdentity
      && access.teamId === teamId
      && access.evaluationIdentity === evaluationIdentity
      && this.items.data.some((team) => team.id === teamId);
  }

  private invalidateTransactionReconciliationAccess(): void {
    this.transactionReconciliationEvaluationGeneration += 1;
    this.transactionReconciliationAccessByTeam.clear();
  }

  private getSessionIdentity(): string {
    const token = this.sessionService.decodeJWTToken();
    const issuedAt = Number(token?.iat);
    const expiresAt = Number(token?.exp);
    return [
      this.sessionService.getUserId(),
      Number.isFinite(issuedAt) ? issuedAt : '',
      Number.isFinite(expiresAt) ? expiresAt : '',
    ].join('|');
  }

  getAllTeams(): void {
    this.invalidateTransactionReconciliationAccess();
    this.teamsService.getAllTeams().then(teams => {
      this.teamCompaniesService.getCompanies().then(companies => {
        this.invalidateTransactionReconciliationAccess();
        this.companies = companies;
        this.items.data = teams;
        this.items.sort = this.sort;
        this.loadCompanyNames();
      }).catch(err => {
        this.items.data = [];
        this.invalidateTransactionReconciliationAccess();
        console.error('Error in get companies', err);
        this.snackBar.openFromComponent(SnackBarComponent, {data: err.error && err.error.message ? err.error.message : 'An error has occurred.'});
      });
    }).catch(err => {
      this.items.data = [];
      this.invalidateTransactionReconciliationAccess();
      console.error('Error in get all teams', err);
      this.snackBar.openFromComponent(SnackBarComponent, {data: err.error && err.error.message ? err.error.message : 'An error has occurred.'});
    });
  }

  private loadCompanyNames(): void {
    this.items.data.forEach((team: any) => {
      // @ts-ignore
      team.company = this.companies.find((company: { id: any; }) => company.domainName?.toLowerCase() === team.ownerEmail?.split('@')[1].toLowerCase())?.name;
    });
  }

  clearFilter(): void {
    this.items.filter = '';
    this.filter = '';
  }

  applyFilter(event: any): void {
    let value = event.target.value;
    value = value.trim(); // Remove whitespace
    value = value.toLowerCase(); // MatTableDataSource defaults to lowercase matches
    this.items.filter = value;
  }

  loginTeamAdmin(user: any): void {
    this.sessionService.loginByUserId(user.ownerUserId);
  }

  loginTeamMember(user: any): void {
    this.uiAlertService.memberLogin(user);
  }

  changeTeamOwner(team: any): void {
    const dialogRef = this.dialog.open(GetUserDialogComponent);
    dialogRef.afterClosed().subscribe((dialogResult: any) => {
      if (dialogResult) {
        this.teamsService.modifyTeam(team.id, {ownerUserId: dialogResult}).then(() => {
          this.snackBar.openFromComponent(SnackBarComponent, {data: `New owner set for team ${team.name}`});
        }).catch(error => {
          this.snackBar.openFromComponent(SnackBarComponent, {data: `Error setting new owner for ${team.name}`});
        });
      }
    });

  }

  isSuperAdmin(): boolean {
    return this.sessionService.isSuperAdmin();
  }

  reconciliation(team: any): void {
    if (typeof team?.id !== 'string' || !this.isTransactionReconciliationEnabled(team.id)) {
      return;
    }
    this.router.navigate(['reconciliation/stripe', team.id]);
  }

  triggerBilling(team: any): void {
    this.teamsService.getCustomerId(team).then(customerId => {
      if (customerId) {
        this.stripeService.triggerBillingCycle(customerId).then(() => {
          this.snackBar.openFromComponent(SnackBarComponent, {data: 'Billing cycle triggered'});
        });
      } else {
        this.snackBar.openFromComponent(SnackBarComponent, {data: 'No billing method configured'});
      }
    });
  }

  transferToWallit(team: any): void {
    this.uiAlertService.superAdminTransfer(team.id, SendMode.TO_WALLIT_BALANCE);
  }

  transferToAllowance(team: any): void {
    this.uiAlertService.superAdminTransfer(team.id, SendMode.TO_ALLOWANCE_BALANCE);
  }

  transferAllowanceToWallit(team: any): void {
    this.uiAlertService.superAdminTransfer(team.id, SendMode.ALLOWANCE_TO_WALLIT);
  }

  approveTeam(team: any): void {
    this.teamsService.modifyTeam(team.id, {approved: 1}).then(result => {
      this.snackBar.openFromComponent(SnackBarComponent, {data: `Team ${team.name} has been approved`});
      team.approved = 1;
      this.checkCurrentWellspace(team);
      this.getAllTeams();
    });
  }

  checkCurrentWellspace(wellspace: any): void {
    this.teamsService.wellspaceChangedObservable.subscribe((res: any) => {
      if (res.id === wellspace.id) {
        this.teamsService.setWellspace(wellspace);
      }
    });
  }

  private reloadItems(): void {
    this.loadCompanyNames();
    this.items.data = this.items.data.slice();
  }

  setCompany(team: any): void {
    const dialogRef = this.dialog.open(SetTeamCompanyDialogComponent, {
      data: new SetTeamCompanyComponentModel(team.companyId, 'Set Team Company',
          'This team can be associated with a single company.', 'Set Company for Team', 'Remove Team Company', false, ['employer'])
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult !== undefined) {
        this.teamsService.modifyTeam(team.id, {companyId: dialogResult});
        team.companyId = dialogResult;
        this.reloadItems();
        this.snackBar.openFromComponent(SnackBarComponent, {data: dialogResult ? `Company set for team ${team.name}` : `Company removed for team ${team.name}`});
      }
    });
  }

  setTransactionPrivacy(team: any): void {
    const dialogRef = this.dialog.open(SetTeamPrivacyDialogComponent, {
      data: new SetTeamPrivacyComponentModel(team.transactionPrivacy)
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult !== undefined) {
        this.teamsService.modifyTeam(team.id, {transactionPrivacy: dialogResult});
        team.transactionPrivacy = dialogResult;
        this.reloadItems();
        this.snackBar.openFromComponent(SnackBarComponent, {data: dialogResult ? `Transaction privacy enabled for team ${team.name}` : `Transaction privacy disabled for team ${team.name}`});
      }
    });
  }

  setInvoicing(team: any): void {
      this.dialog.open(SetInvoicingDialogComponent, {
        data: new SetInvoicingComponentModel(team)
      });
  }

  async checkIfTeamHasSomeAchPending(team: any): Promise<boolean> {
    const pendingCharge: any = await this.teamsService.checkTeamPendingCharges(team.id);
    return !!(pendingCharge && pendingCharge.hasPendingCharges);
  }

  showPendingAchSnack(dataMessage: string): void {
    this.snackBar.openFromComponent(SnackBarComponent, {
      data: dataMessage
    });
  }

  async deleteWellspace(team: any): Promise<void> {
    this.uiAlertService.presentAlertConfirm(`Do you really want to remove this wellspace?`).then(async confirm => {
      if (confirm) {
        const hasAchPending = await this.checkIfTeamHasSomeAchPending(team);
        if (hasAchPending) {
          return this.showPendingAchSnack(
              'A transfer is in progress at the moment. To ensure the integrity of the operation, ' +
              'it is not possible to make further changes at this time. Please wait until the transfer is completed to proceed with your actions.'
          );
        }
        this.teamsService.deleteTeam(team.id).then((): void => {
          this.showPendingAchSnack(`Team ${team.name} removed`);
        });
      }
    });
  }

  teamDetails(team: any): void {
    this.router.navigate(['managewellspace/overview', team.id]);
  }

  openCustomerLinkModal(team: any): void {
    const dialogRef = this.dialog.open(CustomerLinkModalComponent, {data: new CustomerLinkModal(team, this.items.data)});
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        this.getAllTeams();
      }
    });
  }

  createAnInvoice(team: any): void {
    const isInvoicePlan: boolean = team.isInvoicePlan === 0;
    if (team.id && team.id !== '') {
      this.uiAlertService.presentAlertConfirm(`Are you sure you want to create an invoice of team ${team.name} to client ${team?.client?.name ?? ''}?`).then(confirm => {
        if (confirm) {
          this.clientService.createInvoice(team.id, isInvoicePlan).then((): void => {
            this.getAllTeams();
          }).catch(err => {
            this.snackBar.openFromComponent(SnackBarComponent, {data: err.error && err.error.message ? err.error.message : 'An error has occurred.'});
            console.error('Error in create an invoice', err);
          });
        }
      });
    } else {
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'Team not found.'});
    }
  }

  checkClientFeaturePermission(): boolean {
    return this.clientService.checkUserFeaturePermission();
  }

}
